import React, { useMemo, useContext } from 'react';
import styled from 'styled-components';
import { Booth } from 'models/cms/booths';
import { BoothContext } from 'pages/booths/BoothProfileContainer';

import FileArrow from 'components/icons/FileArrow';
import BoothContainer from '../../components/BoothContainer';
import GenericCMSBoothBanner from '../../components/GenericCMSBoothBanner';
import BoothSaleContainer, { SaleInfoContainer, BubbleArrow } from '../BoothSaleContainer';
import BoothBackground from './BoothBackground';

const TopBannerContainer = styled.div`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 368px;
  height: 131px;
  top: -13px;
  left: calc((90% - 283px) / 2);
  z-index: 2;
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: -50px;
`;

const PullUpBannerContainer = styled.div`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 134px;
  height: 273px;
  top: 339px;
  left: calc((90% + 716px) / 2);
  z-index: 2;
`;

const SaleContainer = styled.div`
  position: absolute;
  width: 200px;
  height: 434px;
  left: 905px;
  bottom: -24px;
  z-index: 2;

  & ${SaleInfoContainer} {
    left: -130px;
  }

  & ${BubbleArrow} {
    left: 72%;
  }
`;

const CenterBannerContainer = styled.div`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 237px;
  height: 130px;
  top: 200px;
  left: calc((90% - 153px) / 2);
  z-index: 2;
`;

const RootContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 900px;
`;

const StyledBoothContainer = styled(BoothContainer)`
  left: ${({ needOffset }) => (needOffset ? 'unset' : '-4px')};
  bottom: ${({ needOffset }) => (needOffset ? 'unset' : '0')};
  transform: ${({ needOffset }) => (needOffset ? 'unset' : 'scale(0.8)')};
`;

const BrochureContainer = styled.div<{ enableClick: boolean }>`
  cursor: ${({ enableClick }) => (enableClick ? 'pointer' : 'initial')};
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 102px;
  height: 249px;
  top: 372px;
  left: calc((90% - 790px) / 2);
  z-index: 2;
`;

const ArrowContainer = styled.div`
  position: absolute;
  left: 22px;
  top: 24px;
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const {
    pullUpBanner, topBanner, centerBanner, themeColor,
  } = useMemo(() => {
    const elements = (booth.designElements || []).filter((it) => it?.name);
    return ({
      pullUpBanner: elements.find((designNode) => designNode.name === 'pull_up_banner'),
      topBanner: elements.find((designNode) => designNode.name === 'top_banner'),
      centerBanner: elements.find((designNode) => designNode.name === 'middle_banner'),
      themeColor: elements.find((designNode) => designNode.name === 'theme_color')?.color || '#FFD662',
    });
  }, [booth.designElements]);

  const boothForMatomo = useContext(BoothContext);

  const onBannerClick = (key) => {
    window.trackingItemToFirebase(null, 'Booth', 'Banner', key, key, boothForMatomo.id, boothForMatomo.boothNameForMatomo);
  };

  return (
    <RootContainer>
      <StyledBoothContainer needOffset={!isPreview}>
        <BackgroundContainer>
          <BoothBackground themeColor={themeColor} />
        </BackgroundContainer>
        <TopBannerContainer onClick={() => onBannerClick('Banner 2_Click')}>
          <GenericCMSBoothBanner
            onBannerClick={handleClick as any}
            autoPlayVideo={!isPreview}
            bannerData={topBanner}
          />
        </TopBannerContainer>
        <CenterBannerContainer onClick={() => onBannerClick('Banner 3_Click')}>
          <GenericCMSBoothBanner
            onBannerClick={handleClick as any}
            autoPlayVideo={!isPreview}
            bannerData={centerBanner}
          />
        </CenterBannerContainer>
        <PullUpBannerContainer onClick={() => onBannerClick('Banner 1_Click')}>
          <GenericCMSBoothBanner
            onBannerClick={handleClick as any}
            autoPlayVideo={!isPreview}
            bannerData={pullUpBanner}
          />
        </PullUpBannerContainer>
        {!isPreview && (
          <BrochureContainer enableClick={booth.hasAttachment} onClick={() => handleClick('attachment')}>
            {booth.hasAttachment && (
              <ArrowContainer>
                <FileArrow color={themeColor as any} width={40} height={40} />
              </ArrowContainer>
            )}
          </BrochureContainer>
        )}
        {
          !isPreview && (
            <SaleContainer>
              <BoothSaleContainer booth={booth} sale="male-right" />
            </SaleContainer>
          )
        }
      </StyledBoothContainer>
    </RootContainer>
  );
};

export default Banner;
